import { useEffect, useMemo, useState } from 'react';
import type { ExperienceListItem } from '../types';
import { BookingTypeBadge } from '../components/BookingTypeBadge';
import { useAvailableSlots } from '../hooks/useAvailableSlots';
import { useCreateBooking } from '../hooks/useCreateBooking';
import { usePhoneVerification } from '../hooks/usePhoneVerification';
import { api } from '../services/api';
import { trackEvent } from '../utils/track';
import { DateTimeStep } from './steps/DateTimeStep';
import { PartyStep } from './steps/PartyStep';
import { CustomerStep } from './steps/CustomerStep';
import { ReviewStep } from './steps/ReviewStep';
import { ConfirmationScreen } from './ConfirmationScreen';

interface Customer {
  name: string;
  email: string;
  phone: string;
  cpf: string;
}

export interface BookingFlowState {
  date: string;
  startsAt: string;
  partySize: number;
  customer: Customer;
}

const EMPTY: BookingFlowState = {
  date: '',
  startsAt: '',
  partySize: 1,
  customer: { name: '', email: '', phone: '', cpf: '' },
};

interface BookingFlowProps {
  exp: ExperienceListItem;
}

/**
 * Container do fluxo de reserva (4 passos + confirmação).
 *

 * Submit ramifica pelo bookingType:
 *  - Instant       → useCreateBooking → /booking/create + /payment/create →
 *                     redireciona para o gateway. Pós-pagamento, o backend devolve
 *                     o usuário em /confirmation?token=… (ConfirmationPage existente).
 *  - OnRequest     → POST /booking/request. Backend cria Conversation em
 *                     WaitingAvailability e dispara o pipeline existente do operador
 *                     (template WhatsApp + email + SLA timeout). Não cria Booking nem
 *                     PaymentTransaction — operador confirma via WhatsApp e o link
 *                     de pagamento é enviado depois ao cliente. Mostramos
 *                     ConfirmationScreen com conversationId como código de rastreio.
 */
export function BookingFlow({ exp }: BookingFlowProps) {
  const isInstant = exp.bookingType === 'Confirmação Imediata';
  const [step, setStep] = useState(1);
  const [data, setData] = useState<BookingFlowState>(EMPTY);
  const [confirmed, setConfirmed] = useState(false);

  // partySize=1 sempre — pega TODOS os horários com capacidade. Filtragem vira
  // lógica de UI (Instant bloqueia, Request mostra como intenção).
  const { availabilityType, slots, operatingWindow, loading: loadingSlots } = useAvailableSlots(
    exp.experienceCode,
    data.date || null,
    1,
  );

  const isContinuous = availabilityType === 'Continuous';

  const selectedSlot = useMemo(
    () => slots.find((s) => s.startsAt === data.startsAt) ?? null,
    [slots, data.startsAt],
  );

  // Capacidade só limita Instant — OnRequest é decisão do operador.
  // Continuous tem capacidade diária (operatingWindow.capacityRemaining), não por slot.
  const peopleMax = useMemo(() => {
    if (!isInstant) return 20;
    if (isContinuous) {
      return operatingWindow ? Math.max(1, operatingWindow.capacityRemaining) : 20;
    }
    if (selectedSlot) return Math.max(1, selectedSlot.capacityRemaining);
    if (data.date && slots.length > 0) {
      return Math.max(1, slots.reduce((m, s) => Math.max(m, s.capacityRemaining), 0));
    }
    return 20;
  }, [isInstant, isContinuous, operatingWindow, selectedSlot, data.date, slots]);

  // Clamp pra baixo quando capacidade do slot diminui.
  useEffect(() => {
    if (data.partySize > peopleMax) {
      setData((d) => ({ ...d, partySize: peopleMax }));
    }
  }, [peopleMax, data.partySize]);

  // Limpa slot escolhido se partySize ultrapassar a capacidade dele (Instant SlotBased).
  // Continuous valida pela janela diária — não limpa o horário escolhido.
  useEffect(() => {
    if (isInstant && !isContinuous && selectedSlot && data.partySize > selectedSlot.capacityRemaining) {
      setData((d) => ({ ...d, startsAt: '' }));
    }
  }, [data.partySize, selectedSlot, isInstant, isContinuous]);

  const create = useCreateBooking();
  const verification = usePhoneVerification();
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [trackingId, setTrackingId] = useState<number | null>(null);

  // Funnel: marco de entrada no flow (independente do step depois). Roda
  // uma vez por mount — back/forward dentro do flow nao re-conta.
  useEffect(() => {
    trackEvent('booking_started', {
      experienceCode: exp.experienceCode,
      experienceName: exp.title,
      bookingType: exp.bookingType,
      city: exp.location,
      price: exp.price,
    });
  }, [exp.experienceCode, exp.title, exp.bookingType, exp.location, exp.price]);

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => {
    if (step === 1) {
      if (window.history.length > 1) window.history.back();
      // Link interno pela slug (URL canônica). Cair em /{experienceCode} dispararia
      // um 301 → slug; ir direto na slug evita o salto.
      else window.location.href = `/${encodeURIComponent(exp.slug || exp.experienceCode)}`;
    } else {
      setStep((s) => s - 1);
    }
  };

  const submitting =
    create.stage === 'creating' || create.stage === 'redirecting' || requestSubmitting;
  const submitError = isInstant ? create.error : requestError;

  const handleSubmit = async () => {
    const baseProps = {
      experienceCode: exp.experienceCode,
      experienceName: exp.title,
      bookingType: exp.bookingType,
      city: exp.location,
      price: exp.price,
      partySize: data.partySize,
      eventDate: data.date,
      eventTime: data.startsAt,
    };
    trackEvent('booking_submit_clicked', baseProps);

    if (isInstant) {
      // Marca o ponto de "saiu da nossa SPA pro gateway" — depois disso nao
      // temos mais janela pra reportar nada, entao reporta antes do redirect.
      trackEvent('instant_payment_started', baseProps);
      await create.submit({
        experienceCode: exp.experienceCode,
        startsAt: data.startsAt,
        partySize: data.partySize,
        customer: data.customer,
      });
      // Em sucesso, useCreateBooking chama window.location.assign(paymentUrl).
      // Em erro, create.error popula e o ReviewStep renderiza a mensagem.
      return;
    }

    // OnRequest — POST /booking/request. Backend cria Conversation e aciona o
    // pipeline do operador. Idempotente por (customer, experience, slot) em 1h.
    setRequestSubmitting(true);
    setRequestError(null);
    try {
      const result = await api.requestBooking({
        experienceCode: exp.experienceCode,
        eventDateTime: data.startsAt,
        partySize: data.partySize,
        customer: {
          name: data.customer.name,
          email: data.customer.email,
          phone: data.customer.phone,
          cpf: data.customer.cpf,
        },
      });
      setTrackingId(result.conversationId);
      setConfirmed(true);
      trackEvent('onrequest_submitted', { ...baseProps, conversationId: result.conversationId });
    } catch (e) {
      setRequestError(e instanceof Error ? e.message : 'Erro ao enviar solicitação');
    } finally {
      setRequestSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <ConfirmationScreen
        bookingType={exp.bookingType}
        experienceTitle={exp.title}
        startsAt={data.startsAt}
        partySize={data.partySize}
        customerName={data.customer.name}
        trackingId={trackingId}
      />
    );
  }

  // Step labels — usados no .flow-stepper (sidebar desktop + compact mobile)
  // pra que o usuario sempre saiba ONDE esta no fluxo (data → pessoas → dados
  // → revisao). Indices 0..3 correspondem aos steps 1..4.
  const STEP_LABELS: { title: string; hint: string }[] = [
    { title: 'Data e horário', hint: 'Quando você quer ir' },
    { title: 'Pessoas', hint: 'Tamanho do grupo' },
    { title: 'Seus dados', hint: 'Contato e identificação' },
    { title: 'Revisar', hint: isInstant ? 'Confirmar e pagar' : 'Enviar solicitação' },
  ];

  const cityName = exp.location.split(',').pop()?.trim();
  const cover = exp.images && exp.images.length > 0 ? exp.images[0] : null;

  return (
    <main className="flow">
      <header className="flow-header">
        <button type="button" className="flow-header__back" onClick={back} aria-label="Voltar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flow-header__center">
          <span className="flow-header__step">
            Passo <strong>{step}</strong> de 4 · {STEP_LABELS[step - 1].title}
          </span>
          <BookingTypeBadge type={exp.bookingType} variant="inline" />
        </div>
      </header>

      <div className="flow-progress" aria-hidden="true">
        <div className="flow-progress__bar" style={{ width: `${(step / 4) * 100}%` }} />
      </div>

      {/* Strip de contexto: thumb + cidade-pill + título da experiência.
          Reforça PSICOLOGICAMENTE qual experiência o usuário está reservando
          em cada step. Mesma linguagem visual do .exp-card (home). */}
      <section className="flow-context" aria-label="Experiência selecionada">
        {cover && (
          <div className="flow-context__thumb">
            <img src={cover} alt={exp.title} loading="lazy" decoding="async" />
          </div>
        )}
        <div className="flow-context__body">
          <span className="flow-context__eyebrow">Você está reservando</span>
          <h2 className="flow-context__title">{exp.title}</h2>
          <div className="flow-context__meta">
            {cityName && (
              <span className="flow-context__meta-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {cityName}
              </span>
            )}
            {exp.category && <span className="flow-context__meta-item">{exp.category}</span>}
            {exp.duration && (
              <span className="flow-context__meta-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {(() => {
                  const parts = exp.duration.split(':').map((n) => parseInt(n, 10));
                  const h = parts[0] || 0;
                  const m = parts[1] || 0;
                  if (!h && !m) return exp.duration;
                  if (h && m) return `${h}h${m.toString().padStart(2, '0')}`;
                  if (h) return `${h}h`;
                  return `${m}min`;
                })()}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="flow-layout">
        <div className="flow-body">
          {step === 1 && (
            <DateTimeStep
              date={data.date}
              startsAt={data.startsAt}
              partySize={data.partySize}
              availabilityType={availabilityType}
              slots={slots}
              operatingWindow={operatingWindow}
              loadingSlots={loadingSlots}
              mode={isInstant ? 'instant' : 'request'}
              experienceCode={exp.experienceCode}
              bookingType={exp.bookingType}
              onDateChange={(date) => setData((d) => ({ ...d, date, startsAt: '' }))}
              onSlotChange={(startsAt) => setData((d) => ({ ...d, startsAt }))}
              onNext={next}
            />
          )}
          {step === 2 && (
            <PartyStep
              value={data.partySize}
              max={peopleMax}
              mode={isInstant ? 'instant' : 'request'}
              slotPicked={!!selectedSlot}
              onChange={(partySize) => setData((d) => ({ ...d, partySize }))}
              onNext={next}
            />
          )}
          {step === 3 && (
            <CustomerStep
              value={data.customer}
              verification={verification}
              experienceCode={exp.experienceCode}
              bookingType={exp.bookingType}
              onChange={(customer) => setData((d) => ({ ...d, customer }))}
              onNext={next}
            />
          )}
          {step === 4 && (
            <ReviewStep
              exp={exp}
              data={data}
              submitting={submitting}
              error={submitError}
              onSubmit={handleSubmit}
            />
          )}
        </div>

        {/* Stepper panel: sticky sidebar no desktop, esconde em mobile (a
            barra .flow-progress + .flow-header__step ja dao orientacao em
            telas pequenas). Cada item tem 3 estados: done / current / pending. */}
        <aside className="flow-stepper" aria-label="Etapas da reserva">
          <ol className="flow-stepper__list">
            {STEP_LABELS.map((s, i) => {
              const idx = i + 1;
              const state = idx < step ? 'done' : idx === step ? 'current' : 'pending';
              return (
                <li key={s.title} className={`flow-stepper__item flow-stepper__item--${state}`}>
                  <span className="flow-stepper__dot" aria-hidden="true">
                    {state === 'done' ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    ) : (
                      idx
                    )}
                  </span>
                  <div className="flow-stepper__text">
                    <strong>{s.title}</strong>
                    <small>{s.hint}</small>
                  </div>
                </li>
              );
            })}
          </ol>
        </aside>
      </div>
    </main>
  );
}

